import React from 'react';

import { Skeleton } from '@dashboard/components/ui/skeleton';
import { cn } from '@dashboard/lib/utils';

export function Columns({ sidebar, stage }: { sidebar: React.ReactNode; stage: React.ReactNode }) {
  return (
    <div className="grid min-h-0 gap-4 lg:h-[calc(100vh-13rem)] lg:min-h-[560px] lg:grid-cols-[320px_minmax(0,1fr)]">
      <aside className="flex max-h-[70vh] min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-background lg:max-h-none">
        {sidebar}
      </aside>
      <section className="flex min-h-[480px] min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-muted/40">
        {stage}
      </section>
    </div>
  );
}

export function SidebarHeader({ children }: { children: React.ReactNode }) {
  return <div className="space-y-3 border-b border-border p-3">{children}</div>;
}

export function SidebarList({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <nav aria-label={label} className="min-h-0 flex-1 overflow-y-auto p-2 pb-6">
      {children}
    </nav>
  );
}

export function ListHeading({ children }: { children: React.ReactNode }) {
  return <p className="px-2 pt-3 pb-1 text-xs font-medium text-muted-foreground">{children}</p>;
}

export function ListButton({
  active,
  onClick,
  children,
  aside,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  aside?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted',
        active && 'bg-muted shadow-[inset_2px_0_0_var(--color-primary)]',
      )}
    >
      <span className="min-w-0 flex-1">{children}</span>
      {aside}
    </button>
  );
}

export function ListSkeleton() {
  return (
    <div className="space-y-2 p-2">
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-9 w-full" />
      ))}
    </div>
  );
}

export function StageSkeleton() {
  return (
    <div className="p-4">
      <Skeleton className="mx-auto h-[480px] w-full max-w-[960px]" />
    </div>
  );
}

export function StagePlaceholder({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="m-auto max-w-sm p-6 text-center text-sm text-muted-foreground">
      <p className="mb-1 text-base font-medium text-foreground">{title}</p>
      {children}
    </div>
  );
}
